import { Button, Dialog, DialogPanel, TextInput, Textarea } from "@tremor/react";
import Badge from "../components/Badge.jsx";
import {
  ChevronRight,
  Globe,
  Layers,
  Plus,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  useCreateGroup,
  useGroups,
  useQuickSetup,
} from "../queries/groups.js";
import { useLocations } from "../queries/monitors.js";
import DeleteGroupDialog from "../components/DeleteGroupDialog.jsx";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_BADGE_COLOR = { up: "emerald", degraded: "amber", down: "red", paused: "gray" };

const STATUS_DOT = {
  up: "bg-green-500",
  degraded: "bg-yellow-400",
  down: "bg-red-500",
  paused: "bg-gray-400",
};

// ---------------------------------------------------------------------------
// Quick-Setup Wizard
// ---------------------------------------------------------------------------

const WIZARD_STEPS = ["Website", "Checks", "Confirm"];

function QuickSetupWizard({ onClose, onCreated }) {
  const { t } = useTranslation("groups");
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");

  const checkOptions = Object.entries(t("check_options", { returnObjects: true })).map(([type, meta]) => ({
    type,
    label: meta.label,
    description: meta.description,
    defaultOn: ["http", "ssl", "domain"].includes(type),
  }));

  const [selectedChecks, setSelectedChecks] = useState(
    new Set(checkOptions.filter((c) => c.defaultOn).map((c) => c.type))
  );
  const [locations, setLocations] = useState([]);
  const quickSetupMutation = useQuickSetup();
  const saving = quickSetupMutation.isPending;

  const wizardSteps = Object.values(t("wizard.steps", { returnObjects: true }));

  const { data: availableLocations = [] } = useLocations();
  // Default to the first location once data arrives.
  useEffect(() => {
    if (availableLocations.length > 0 && locations.length === 0) {
      setLocations([availableLocations[0].slug]);
    }
  }, [availableLocations, locations.length]);

  function toggleCheck(type) {
    setSelectedChecks((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function toggleLocation(r) {
    setLocations((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
    );
  }

  async function handleFinish() {
    try {
      const group = await quickSetupMutation.mutateAsync({
        name,
        url,
        checks: [...selectedChecks],
        locations,
        interval_seconds: 60,
      });
      toast.success(t("wizard.success", { name: group.name, count: group.member_count }));
      onCreated(group);
      onClose();
    } catch {
      toast.error(t("errors.create_error"));
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-md flex-col rounded-none bg-white p-0 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t("wizard.title")}</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {t("wizard.step_indicator", { current: step + 1, total: wizardSteps.length, name: wizardSteps[step] })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex border-b border-gray-100">
          {wizardSteps.map((s, i) => (
            <div
              key={s}
              className={`flex-1 py-2 text-center text-xs font-medium ${
                i === step
                  ? "border-b-2 border-teal-600 text-teal-600"
                  : i < step
                  ? "text-green-600"
                  : "text-gray-400"
              }`}
            >
              {s}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {t("wizard.group_name")}
                </label>
                <TextInput
                  autoFocus
                  value={name}
                  onValueChange={setName}
                  placeholder="Acme Corp"
                />
                <p className="mt-1 text-xs text-gray-400">
                  {t("wizard.group_name_help")}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {t("wizard.website_url")}
                </label>
                <TextInput
                  value={url}
                  onValueChange={setUrl}
                  placeholder="https://acme-corp.com"
                />
                <p className="mt-1 text-xs text-gray-400">
                  {t("wizard.website_url_help")}
                </p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <p className="text-sm text-gray-600">
                {t("wizard.checks_title", { name })}
              </p>
              <div className="space-y-2">
                {checkOptions.map((c) => (
                  <label
                    key={c.type}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedChecks.has(c.type)}
                      onChange={() => toggleCheck(c.type)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{c.label}</p>
                      <p className="text-xs text-gray-500">{c.description}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div>
                <label className="mb-2 block text-xs font-medium text-gray-600">
                  {t("wizard.locations")}
                </label>
                <div className="grid grid-cols-2 gap-1.5">
                  {availableLocations.map((loc) => (
                    <label
                      key={loc.slug}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={locations.includes(loc.slug)}
                        onChange={() => toggleLocation(loc.slug)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                      />
                      <span className="font-mono text-xs">{loc.label}</span>
                    </label>
                  ))}
                </div>
                {locations.length === 0 && (
                  <p className="mt-1 text-xs text-red-500">
                    {t("wizard.locations_empty")}
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {t("wizard.confirm_title")}
              </p>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t("wizard.confirm_group")}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-900">{name}</p>
                  <p className="text-xs text-gray-500">{url}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t("wizard.confirm_monitors")}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {[...selectedChecks].map((tkey) => (
                      <Badge key={tkey} color="teal">{tkey.toUpperCase()}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t("wizard.confirm_locations")}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {locations.map((r) => (
                      <Badge key={r} color="gray">{r}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <Button
            variant="secondary"
            type="button"
            onClick={() => (step === 0 ? onClose() : setStep((s) => s - 1))}
          >
            {step === 0 ? t("wizard.back") : t("wizard.back")}
          </Button>
          {step < wizardSteps.length - 1 ? (
            <Button
              type="button"
              disabled={
                (step === 0 && (!name.trim() || !url.trim())) ||
                (step === 1 && (selectedChecks.size === 0 || locations.length === 0))
              }
              onClick={() => setStep((s) => s + 1)}
            >
              {t("wizard.next")}
            </Button>
          ) : (
            <Button type="button" onClick={handleFinish} disabled={saving}>
              {saving ? t("wizard.creating") : t("wizard.create")}
            </Button>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// New Group (manual) drawer
// ---------------------------------------------------------------------------

function NewGroupDrawer({ onClose, onCreated }) {
  const { t } = useTranslation("groups");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const createGroupMutation = useCreateGroup();
  const saving = createGroupMutation.isPending;

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const group = await createGroupMutation.mutateAsync({
        name,
        description: description || undefined,
      });
      toast.success(t("drawer.success", { name: group.name }));
      onCreated(group);
      onClose();
    } catch {
      toast.error(t("errors.create_error"));
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-md flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">{t("drawer.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("drawer.name")}</label>
              <TextInput
                required
                autoFocus
                value={name}
                onValueChange={setName}
                placeholder="Acme Corp"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("drawer.description")}{" "}
                <span className="font-normal text-gray-400">{t("drawer.optional")}</span>
              </label>
              <Textarea
                rows={3}
                value={description}
                onValueChange={setDescription}
                placeholder={t("drawer.description_placeholder")}
              />
            </div>
            <p className="text-xs text-gray-400">
              {t("drawer.help")}
            </p>
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>
              {t("common:common.cancel")}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("drawer.creating") : t("drawer.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Group card
// ---------------------------------------------------------------------------

function GroupCard({ group, onDelete }) {
  const { t } = useTranslation("groups");
  const [showDelete, setShowDelete] = useState(false);

  function openDelete(e) {
    e.preventDefault();
    e.stopPropagation();
    setShowDelete(true);
  }

  return (
    <>
    <Link
      to={`/groups/${group.id}`}
      className="group relative flex flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition hover:border-teal-300 hover:shadow-md"
    >
      {/* Status dot */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`mt-0.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${STATUS_DOT[group.status] ?? STATUS_DOT.up}`}
          />
          <h3 className="text-sm font-semibold text-gray-900">{group.name}</h3>
        </div>
        <button
          type="button"
          onClick={openDelete}
          className="rounded p-1 text-gray-300 opacity-0 transition hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {group.description && (
        <p className="mt-1.5 text-xs text-gray-500 line-clamp-2">{group.description}</p>
      )}

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Layers className="h-3.5 w-3.5" />
          <span>{t("card.monitors_count", { count: group.member_count })}</span>
        </div>
        <Badge color={STATUS_BADGE_COLOR[group.status] ?? "gray"}>
          {t(`status.${group.status}`)}
        </Badge>
      </div>

      <ChevronRight className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-300 transition group-hover:text-teal-400" />
    </Link>
    {showDelete && (
      <DeleteGroupDialog
        group={group}
        onClose={() => setShowDelete(false)}
        onDeleted={onDelete}
      />
    )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Groups page
// ---------------------------------------------------------------------------

export default function Groups() {
  const { t } = useTranslation(["groups", "common"]);
  const [showWizard, setShowWizard] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);

  const groupsQuery = useGroups();
  const groups = groupsQuery.data ?? [];
  const loading = groupsQuery.isPending;

  // Mutation hooks invalidate the groups list automatically; these handlers
  // exist only to close the drawer/wizard after a successful create.
  function handleCreated() {}
  function handleDeleted() {}

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t("subtitle")}
          </p>
        </div>
        <p className="text-sm text-gray-400">{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t("subtitle")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button icon={Zap} onClick={() => setShowWizard(true)}>
            {t("quick_setup")}
          </Button>
          <Button variant="secondary" icon={Plus} onClick={() => setShowNewGroup(true)}>
            {t("new_group")}
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {groups.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-20 text-center">
          <Globe className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-4 text-base font-semibold text-gray-700">{t("empty.title")}</p>
          <p className="mt-1 max-w-sm text-sm text-gray-400">
            {t("empty.description")}
          </p>
          <Button className="mt-6" icon={Zap} onClick={() => setShowWizard(true)}>
            {t("quick_setup")}
          </Button>
        </div>
      )}

      {/* Group cards grid */}
      {groups.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <GroupCard key={g.id} group={g} onDelete={handleDeleted} />
          ))}
        </div>
      )}

      {/* Dialogs */}
      {showWizard && (
        <QuickSetupWizard
          onClose={() => setShowWizard(false)}
          onCreated={handleCreated}
        />
      )}
      {showNewGroup && (
        <NewGroupDrawer
          onClose={() => setShowNewGroup(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
